/*
 * File: DriveCountdown.tsx
 * Author: Austin Robinson
 * Date Created: 12/09/2025
 * Copyright (c) DrivingDoc 2025
 *
 * Purpose: Real-time countdown display for driving periods
 * Uses react-countdown library for efficient countdown management
 */
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import Countdown, { CountdownRenderProps } from 'react-countdown';

// Enable required Day.js plugins
dayjs.extend(relativeTime);

/**
 * Props for the DriveCountdown component
 */
interface DriveCountdownProps {
  /** Start time of the driving period (ISO string or Date) */
  startTime: string | Date;
  /** End time of the driving period (ISO string or Date) */
  endTime: string | Date;
}

/**
 * DriveCountdown Component
 *
 * Displays a real-time countdown for driving periods with smart status messages:
 * - Before start: Countdown to start time (e.g., "Starts in 5m", "Starts in 2h 30m")
 * - During period: "In progress • Ends in 45m"
 * - After end: "Completed"
 *
 * Color coding:
 * - Green (success.main): Completed
 * - Orange (warning.main): In progress
 * - Blue (primary.main): Starting soon (<60 min)
 * - Gray (text.secondary): Upcoming (60+ min)
 *
 * @example
 * ```tsx
 * <DriveCountdown startTime={period.start} endTime={period.end} />
 * ```
 */
export function DriveCountdown({ startTime, endTime }: DriveCountdownProps) {
  // Validate dates
  const start = dayjs(startTime);
  const end = dayjs(endTime);

  if (!start.isValid() || !end.isValid()) {
    return (
      <Typography variant="body1" color="text.disabled" fontWeight="medium">
        Loading...
      </Typography>
    );
  }

  // Determine current state
  const now = dayjs();
  const isCompleted = now.isAfter(end);
  const isInProgress = now.isAfter(start) && !isCompleted;
  const minutesUntilStart = start.diff(now, 'minute');

  // State 1: Completed
  if (isCompleted) {
    return (
      <Typography variant="body1" color="success.main" fontWeight="bold">
        Completed
      </Typography>
    );
  }

  // State 2: In Progress
  if (isInProgress) {
    return (
      <Typography variant="body1" color="primary.main" fontWeight="medium">
        In Progress
      </Typography>
    );
  }

  // State 3: Upcoming (Starting Soon or Far Future)
  const isStartingSoon = minutesUntilStart < 60;
  const color = isStartingSoon ? 'primary.main' : 'text.secondary';
  const fontWeight = isStartingSoon ? 'bold' : 'medium';

  return (
    <Countdown
      date={start.toDate()}
      renderer={({ hours, minutes, total }: CountdownRenderProps) => {
        const formatStartTime = () => {
          if (total >= 86400000) {
            // 24+ hours (use relative format)
            return start.fromNow();
          }
          if (hours > 0) {
            return minutes > 0 ? `Starts in ${hours}h ${minutes}m` : `Starts in ${hours}h`;
          }
          return `Starts in ${minutes}m`;
        };

        return (
          <Typography variant="body1" color={color} fontWeight={fontWeight}>
            {formatStartTime()}
          </Typography>
        );
      }}
    />
  );
}

export default DriveCountdown;
